/**
 * SportsArticleDetailPage.mjs
 * 
 * Detail page for StreamlinedSourceArticle documents in the Sports UCX.
 * Displays rich taxonomy (categories, topics, tags), bias analysis,
 * source articles, persona information, and AI conversation.
 */
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import { setPageTitle, setPageDescription, setCanonicalUrl, setPageMetadata, resetPageMetadata } from '../slices/pageMetadataSlice.mjs';
import { setHeaderData } from '../slices/headerSlice.mjs';
import { resetConversation, setConversationExpanded, selectIsConversationExpanded, toggleConversationExpanded } from '../slices/conversationSlice.mjs';
import useIsMounted from '../hooks/useIsMounted.mjs';
import ChatPanel from '../components/conversation/ChatPanel.mjs';
import getArticleLayout from './article/layouts/index.mjs';
import ShareOnX from '../components/social/ShareOnX.mjs';
import Breadcrumbs from '../components/navigation/Breadcrumbs.mjs';
import ArticleNavBar from '../components/navigation/ArticleNavBar.mjs';
import { registerNavSections, clearNavSections } from '../slices/articleNavSlice.mjs';
import { buildCanonicalArticleUrl } from '../utils/buildArticleUrl.mjs';
import { fetchBulkRatingAggregates } from '../slices/ratingsSlice.mjs';
import StarRating from '../components/ratings/StarRating.mjs';

const LARGE_BREAKPOINT_QUERY = '(min-width: 1024px)';

/**
 * Badge component for displaying topics with confidence levels
 */
const TopicBadge = ({ topic }) => {
    const confidenceColor = topic.confidence >= 0.8
        ? 'bg-theme-badge-success-bg text-theme-badge-success-text'
        : topic.confidence >= 0.5
            ? 'bg-theme-badge-warning-bg text-theme-badge-warning-text'
            : 'bg-theme-badge-neutral-bg text-theme-badge-neutral-text';

    return (
        <span className={`inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium border border-white/10 backdrop-blur-sm flex-shrink-0 whitespace-nowrap ${confidenceColor}`}>
            <span className="mr-1">{topic.label || topic.code}</span>
            {topic.confidence && (
                <span className="text-[10px] opacity-70">
                    {Math.round(topic.confidence * 100)}%
                </span>
            )}
        </span>
    );
};

/**
 * Expandable topic badge row.
 * Mobile: all topics in a single horizontal-scroll row (no expand/collapse).
 * Desktop (md+): shows top N with expand/collapse; expanded = horizontal scroll.
 * Mouse-wheel scrolls horizontally without Shift.
 */
const TopicBadgeRow = ({ topics, maxVisible = 3 }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const scrollRef = useRef(null);
    const overflowCount = topics.length - maxVisible;

    // Convert vertical wheel → horizontal scroll
    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const onWheel = (e) => {
            if (Math.abs(e.deltaY) > Math.abs(e.deltaX)) {
                e.preventDefault();
                el.scrollLeft += e.deltaY;
            }
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [isExpanded]);

    return (
        <div
            ref={scrollRef}
            className={`flex items-center gap-2 min-w-0
                flex-nowrap overflow-x-auto
                ${isExpanded ? 'md:flex-nowrap md:overflow-x-auto' : 'md:flex-wrap md:overflow-x-visible'}
            `}
            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none', WebkitOverflowScrolling: 'touch' }}
        >
            {topics.map((topic, idx) => {
                const badge = <TopicBadge key={idx} topic={topic} />;
                // Overflow badges: always visible on mobile, hidden on desktop when collapsed
                if (idx >= maxVisible && !isExpanded) {
                    return <span key={idx} className="contents md:hidden">{badge}</span>;
                }
                return badge;
            })}
            {/* Show more/less button — desktop only */}
            {overflowCount > 0 && (
                <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="hidden md:inline-flex items-center px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap
                        bg-theme-surface-muted text-theme-text-muted hover:bg-theme-surface-elevated
                        border border-white/10 backdrop-blur-sm transition-colors duration-theme cursor-pointer flex-shrink-0"
                >
                    {isExpanded ? 'Show less' : `+${overflowCount} more`}
                </button>
            )}
        </div>
    );
};

/**
 * Bias indicator component
 */
const BiasIndicator = ({ bias, biases }) => {
    if (!bias && (!biases || biases.length === 0)) return null;

    return (
        <div className="sports-article-bias-section mb-6 p-4 bg-theme-warning/10 rounded-lg border border-theme-warning/30">
            <h3 className="text-sm font-semibold text-theme-warning mb-2">
                Bias Analysis
            </h3>
            {bias && (
                <p className="text-sm text-theme-text-muted mb-2">{bias}</p>
            )}
            {biases && biases.length > 0 && (
                <div className="space-y-2">
                    {biases.map((b, idx) => (
                        <div key={idx} className="text-sm">
                            <span className="font-medium text-theme-warning">
                                {b.type}:
                            </span>
                            <span className="ml-2 text-theme-text-muted">
                                {b.explanation || b.description}
                            </span>
                            {(b.score !== undefined || b.severity) && (
                                <span className="ml-2 text-xs opacity-70">
                                    (Score: {b.score ?? b.severity})
                                </span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

/**
 * Source article card component
 */
const SourceArticleCard = ({ source }) => {
    if (!source) return null;

    // Format score safely
    const formatScore = (score) => {
        if (typeof score !== 'number') return null;
        return `${(score * 100).toFixed(0)}%`;
    };

    return (
        <div className="sports-source-card p-3 bg-theme-surface-muted rounded-lg border border-theme-border">
            <a
                href={source.url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-theme-link hover:text-theme-link-hover hover:underline font-medium text-sm"
            >
                {source.title || 'Untitled Source'}
            </a>
            <div className="flex items-center mt-1 text-xs text-theme-text-muted">
                {source.site && (
                    <span className="mr-3">{source.site}</span>
                )}
                {formatScore(source.score) && (
                    <span className="mr-3">Score: {formatScore(source.score)}</span>
                )}
                {source.publicationDate && (
                    <span>{new Date(source.publicationDate).toLocaleDateString('en-US', { timeZone: 'UTC' })}</span>
                )}
            </div>
        </div>
    );
};

/**
 * Collapsible persona section
 */
const PersonaSection = ({ persona }) => {
    const [isExpanded, setIsExpanded] = useState(false);

    if (!persona) return null;

    return (
        <div className="sports-persona-section md:w-1/2 md:min-w-0 relative border border-theme-border rounded-lg overflow-visible">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full px-3 py-2 flex items-center justify-between bg-theme-surface-muted hover:bg-theme-surface-elevated transition-colors rounded-lg"
            >
                <span className="font-medium text-theme-text text-xs truncate mr-2">
                    Generated by: {persona.name || 'AI Persona'}
                </span>
                <svg
                    className={`w-4 h-4 flex-shrink-0 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
            </button>
            {isExpanded && (
                <div className="absolute left-0 right-0 top-full z-20 mt-1 px-4 py-3 bg-theme-surface text-sm space-y-2 border border-theme-border rounded-lg shadow-lg">
                    {persona.place_of_residence && (
                        <p><span className="font-medium">Location:</span> {persona.place_of_residence}</p>
                    )}
                    {persona.writing_style?.tone && (
                        <p><span className="font-medium">Writing Tone:</span> {persona.writing_style.tone}</p>
                    )}
                    {persona.values_and_beliefs?.political_alignment && (
                        <p><span className="font-medium">Perspective:</span> {persona.values_and_beliefs.political_alignment}</p>
                    )}
                    {persona.interests?.professional_interests?.length > 0 && (
                        <p><span className="font-medium">Interests:</span> {persona.interests.professional_interests.join(', ')}</p>
                    )}
                </div>
            )}
        </div>
    );
};

/**
 * Article image component for inline display
 */
const ArticleImage = ({ image, isHero = false }) => {
    if (!image?.url) return null;

    return (
        <figure className={`sports-article-image ${isHero ? 'mb-8' : 'my-6'}`}>
            <img
                src={image.url}
                alt={image.alt || 'Article image'}
                className={`w-full rounded-lg shadow-md ${isHero ? 'aspect-video object-cover' : ''}`}
                loading={isHero ? 'eager' : 'lazy'}
            />
            {image.alt && (
                <figcaption className="mt-2 text-sm text-theme-text-muted text-center italic">
                    {image.alt}
                </figcaption>
            )}
        </figure>
    );
};

/**
 * Compute image placement map for interleaving with paragraphs.
 * Returns { heroImage, imagesByParagraph } where imagesByParagraph[idx] = [images after paragraph idx]
 * 
 * @param {Array} generatedImages - Array of image objects
 * @param {number} paragraphCount - Number of body paragraphs
 * @returns {{ heroImage: Object|null, imagesByParagraph: Object }}
 */
const computeImagePlacement = (generatedImages, paragraphCount) => {
    const images = generatedImages || [];
    if (images.length === 0) {
        return { heroImage: null, imagesByParagraph: {} };
    }

    // Check if any images have explicit afterParagraphIndex
    const hasExplicitPlacement = images.some(img => typeof img.afterParagraphIndex === 'number');

    if (hasExplicitPlacement) {
        // Use explicit placement
        let heroImage = null;
        const imagesByParagraph = {};

        for (const img of images) {
            const idx = img.afterParagraphIndex;
            if (idx === -1 || (idx === undefined && !heroImage)) {
                // Hero image (before body) - take first one only
                if (!heroImage) heroImage = img;
            } else if (typeof idx === 'number' && idx >= 0) {
                if (!imagesByParagraph[idx]) imagesByParagraph[idx] = [];
                imagesByParagraph[idx].push(img);
            }
        }

        // Images without explicit placement and not hero go to unassigned
        const unassignedImages = images.filter(img =>
            img.afterParagraphIndex === undefined && img !== heroImage
        );

        // Distribute unassigned images evenly in remaining gaps
        if (unassignedImages.length > 0 && paragraphCount > 0) {
            const usedIndices = new Set(Object.keys(imagesByParagraph).map(Number));
            const availableIndices = [];
            for (let i = 0; i < paragraphCount; i++) {
                if (!usedIndices.has(i)) availableIndices.push(i);
            }

            const interval = Math.max(1, Math.ceil(availableIndices.length / unassignedImages.length));
            unassignedImages.forEach((img, i) => {
                const targetIdx = availableIndices[Math.min(i * interval, availableIndices.length - 1)];
                if (targetIdx !== undefined) {
                    if (!imagesByParagraph[targetIdx]) imagesByParagraph[targetIdx] = [];
                    imagesByParagraph[targetIdx].push(img);
                }
            });
        }

        return { heroImage, imagesByParagraph };
    }

    // Fallback: Even distribution (no explicit placement)
    // First image is hero, rest are distributed evenly
    const heroImage = images[0];
    const inlineImages = images.slice(1);
    const imagesByParagraph = {};

    if (inlineImages.length > 0 && paragraphCount > 0) {
        // Calculate interval for even distribution (after which paragraphs to place images)
        // E.g., 3 images, 9 paragraphs → place after paragraphs 2, 5, 8 (interval ~3)
        const interval = Math.max(1, Math.floor(paragraphCount / (inlineImages.length + 1)));

        inlineImages.forEach((img, i) => {
            // Place after paragraph: interval * (i + 1) - 1, but cap at paragraphCount - 1
            const targetIdx = Math.min((interval * (i + 1)) - 1, paragraphCount - 1);
            if (!imagesByParagraph[targetIdx]) imagesByParagraph[targetIdx] = [];
            imagesByParagraph[targetIdx].push(img);
        });
    }

    return { heroImage, imagesByParagraph };
};

/**
 * Main sports article detail content
 */
const SportsArticleDetailContent = () => {
    const params = useParams();
    const slug = params.slug;  // Now using slug instead of articleId
    const dispatch = useDispatch();
    const isMounted = useIsMounted();

    // User state for conversation
    const { user } = useSelector((state) => state.auth);

    // Conversation state
    const isConversationGloballyExpanded = useSelector(selectIsConversationExpanded);
    const [isLargeScreen, setIsLargeScreen] = useState(false);
    const [summaryHeight, setSummaryHeight] = useState(null);
    const summaryRef = useRef(null);

    // Get article from Redux state - check both currentArticle and articlesByScope
    const {
        currentArticle,
        currentArticleId,
        isLoadingArticle,
        articleError,
        articlesByScope,
    } = useSelector((state) => state.unifiedArticles || {});

    // Look up article: prioritize currentArticle for SSR, then slug lookup for client navigation
    const article = React.useMemo(() => {
        // SSR case: If we have currentArticle and slug is undefined/empty, use it directly
        // This handles the case where SSR controller preloads the article but useParams() doesn't work
        if (currentArticle && !slug) {
            return currentArticle;
        }

        // If currentArticle matches the slug, use it
        if (currentArticle && slug && currentArticle.slug === slug) {
            return currentArticle;
        }

        // Also check if currentArticle matches by _id for backwards compat
        if (currentArticle && slug && currentArticle._id?.toString() === slug) {
            return currentArticle;
        }

        // Otherwise, search in articlesByScope (client-side navigation)
        // articlesByScope is a map of scopeKey -> articles array
        if (slug && articlesByScope && typeof articlesByScope === 'object') {
            for (const scopeKey of Object.keys(articlesByScope)) {
                const scopeArticles = articlesByScope[scopeKey];
                if (Array.isArray(scopeArticles)) {
                    for (const group of scopeArticles) {
                        if (group?.articles) {
                            const found = group.articles.find(a => a.slug === slug || a._id?.toString() === slug);
                            if (found) return found;
                        }
                    }
                }
            }
        }

        return null;
    }, [currentArticle, slug, articlesByScope]);

    // Update page metadata when article loads
    useEffect(() => {
        if (article) {
            const pageTitle = article.title || 'Sports Article';
            dispatch(setPageTitle(pageTitle));
            const rawDesc = article.summary || 'Read the full article';
            const metaDesc = rawDesc.length <= 160 ? rawDesc
                : (rawDesc.lastIndexOf(' ', 159) > 0
                    ? rawDesc.substring(0, rawDesc.lastIndexOf(' ', 159))
                    : rawDesc.substring(0, 159)) + '\u2026';
            dispatch(setPageDescription(metaDesc));
            // Set canonical URL for SEO — always points to the article's
            // primary category/topic regardless of navigated-from context
            const baseUrl = typeof window !== 'undefined'
                ? `${window.location.protocol}//${window.location.host}`
                : '';
            const canonicalUrl = buildCanonicalArticleUrl(article, baseUrl);
            dispatch(setCanonicalUrl(canonicalUrl));
            // Set OG/Twitter meta tags for social sharing
            const articleImage = article.generatedImages?.[0]?.url || article.images?.[0]?.url || null;
            dispatch(setPageMetadata({
                'og:title': pageTitle,
                'og:description': metaDesc,
                'og:type': 'article',
                'og:url': canonicalUrl,
                ...(articleImage ? { 'og:image': articleImage } : {}),
                'twitter:card': articleImage ? 'summary_large_image' : 'summary',
                'twitter:title': pageTitle,
                'twitter:description': metaDesc,
                ...(articleImage ? { 'twitter:image': articleImage } : {}),
            }));
            dispatch(setHeaderData({
                isArticlePage: true,
                title: 'Nexus of Truth - Sports',
                description: article.summary?.substring(0, 100) || '',
            }));
        }
        return () => {
            dispatch(resetPageMetadata());
        };
    }, [article, dispatch]);

    // Get article ID for conversation context (defined before useEffect that needs it)
    const articleId = article?._id?.toString() || currentArticleId;

    // Reset conversation when article changes
    useEffect(() => {
        if (articleId) {
            dispatch(resetConversation());
        }
    }, [articleId, dispatch]);

    // Fetch rating aggregates for this article — thunk handles staleness/dedup
    useEffect(() => {
        if (articleId) {
            dispatch(fetchBulkRatingAggregates({ articleIds: [articleId] }));
        }
    }, [articleId, dispatch]);

    // Register nav sections for bottom navigation bar
    useEffect(() => {
        dispatch(registerNavSections([
            { id: 'section-summary', label: 'Summary', icon: 'summary' },
            { id: 'section-article', label: 'Article', icon: 'article' },
            { id: 'section-facts', label: 'Facts', icon: 'facts' },
            { id: 'section-sources', label: 'Sources', icon: 'sources' },
            { id: 'section-chat', label: 'Chat', icon: 'chat', fallbackSelector: '.article-conversation-container' },
        ]));
        return () => dispatch(clearNavSections());
    }, [dispatch]);

    // Auto-expand conversation when navigated with ?chat=open
    useEffect(() => {
        if (!isMounted) return;
        const params = new URLSearchParams(window.location.search);
        if (params.get('chat') === 'open') {
            dispatch(setConversationExpanded(true));
        }
    }, [isMounted, dispatch]);

    // Track screen size for layout
    useEffect(() => {
        const mediaQueryList = window.matchMedia(LARGE_BREAKPOINT_QUERY);
        const handleResize = () => {
            setIsLargeScreen(mediaQueryList.matches);
        };
        setIsLargeScreen(mediaQueryList.matches);
        mediaQueryList.addEventListener('change', handleResize);
        return () => mediaQueryList.removeEventListener('change', handleResize);
    }, []);

    // Measure summary height for conversation positioning
    useEffect(() => {
        if (!isLargeScreen || !article?.summary) return;

        const measureSummary = () => {
            if (!summaryRef.current) return;
            const summaryRect = summaryRef.current.getBoundingClientRect();
            setSummaryHeight(Math.ceil(summaryRect.height) + 2);
        };

        let resizeObserver;
        if (typeof ResizeObserver !== 'undefined') {
            try {
                resizeObserver = new ResizeObserver(() => {
                    if (summaryRef.current) {
                        requestAnimationFrame(measureSummary);
                    }
                });
                if (summaryRef.current) {
                    resizeObserver.observe(summaryRef.current);
                }
            } catch (error) {
                console.error('ResizeObserver error:', error);
            }
        }

        window.addEventListener('resize', measureSummary);
        requestAnimationFrame(measureSummary);

        return () => {
            if (resizeObserver) resizeObserver.disconnect();
            window.removeEventListener('resize', measureSummary);
        };
    }, [article?.summary, isLargeScreen]);


    const renderLink = (to, className, children) => {
        if (isMounted) {
            return <Link to={to} className={className}>{children}</Link>;
        }
        return <a href={to} className={className}>{children}</a>;
    };

    // Normalize categories to always be an array
    // IMPORTANT: This useMemo MUST be above the early returns to satisfy
    // React's rules of hooks (hooks must run in the same order every render).
    const categories = React.useMemo(() => {
        if (!article?.categories) return [];
        if (Array.isArray(article.categories)) return article.categories;
        if (typeof article.categories === 'string') return [article.categories];
        return [];
    }, [article?.categories]);

    // Loading state
    if (isLoadingArticle && !article) {
        return (
            <div className="sports-article-loading flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-theme-primary"></div>
                <span className="ml-3 text-theme-text-muted">Loading article...</span>
            </div>
        );
    }

    // Error state
    if (articleError) {
        // Handle both string and object error formats
        const errorMessage = typeof articleError === 'string'
            ? articleError
            : articleError?.message || 'An error occurred';

        return (
            <div className="sports-article-error text-center py-20">
                <h2 className="text-xl font-semibold text-theme-error mb-2">
                    Error loading article
                </h2>
                <p className="text-theme-text-muted mb-4">{errorMessage}</p>
                {renderLink('/', 'text-theme-link hover:underline', '← Back to Home')}
            </div>
        );
    }

    // Not found state
    if (!article) {
        return (
            <div className="sports-article-not-found text-center py-20">
                <h2 className="text-xl font-semibold text-theme-text mb-2">
                    Article not found
                </h2>
                <p className="text-theme-text-muted mb-4">
                    The requested article could not be found.
                </p>
                {renderLink('/', 'text-theme-link hover:underline', '← Back to Home')}
            </div>
        );
    }

    // Render article content

    // Determine grid layout class based on conversation expansion
    const gridLayoutClass = `sports-article-layout-grid ${isConversationGloballyExpanded && isLargeScreen ? 'conversation-expanded' : ''
        }`.trim();

    return (
        <div className="home-page-container sports-article-detail">
            <Breadcrumbs />

            {/* Grid layout for large screens */}
            <div className={isLargeScreen ? gridLayoutClass : ''}>
                {/* Left column - Conversation (large screens only) */}
                {isLargeScreen && (
                    <div className="sports-article-layout-left">
                        {article.summary && (
                            <div ref={summaryRef} className="sports-article-summary-column lg:sticky lg:top-24">
                                <h3 className="text-sm font-semibold text-theme-text mb-2">Summary</h3>
                                <div className="text-sm text-theme-text-muted mb-4">
                                    <p>{article.summary}</p>
                                </div>
                            </div>
                        )}

                        {!user && article && (
                            <div className="sports-article-summary-column mt-6 lg:sticky lg:top-24">
                                <p className="text-sm text-theme-text-muted p-4">
                                    {renderLink('/login', 'text-theme-link hover:underline', 'Log in')}{' '}
                                    or{' '}
                                    {renderLink('/signup', 'text-theme-link hover:underline', 'sign up')}{' '}
                                    to chat with the AI persona about this article.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                {/* Main content column */}
                <div className={isLargeScreen ? 'sports-article-layout-center' : ''}>
                    {/* Title */}
                    <h1 className="sports-article-title text-3xl md:text-4xl font-bold text-theme-text mb-4 leading-tight">
                        {article.title}
                    </h1>

                    {/* Share row */}
                    <div className="flex items-center gap-2 mb-4">
                        <ShareOnX article={article} title={article.title} variant="detail" />
                        {user ? (
                            <button
                                className="article-chat-btn"
                                onClick={() => dispatch(toggleConversationExpanded())}
                                aria-label="Open chat"
                                title="Chat about this article"
                            >
                                <svg className="article-chat-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                                    <path d="M8 9h8M8 13h5" />
                                </svg>
                            </button>
                        ) : (
                            renderLink('/signup', 'article-chat-btn article-chat-btn--guest', <>
                                <svg className="article-chat-btn__icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" />
                                    <path d="M8 9h8M8 13h5" />
                                </svg>
                            </>)
                        )}
                        <StarRating articleId={articleId} variant="detail" />
                    </div>

                    {/* Persona + Topic badges — inline on md+, stacked on mobile */}
                    <div className="flex flex-col md:flex-row md:items-start gap-3 mb-6">
                        <PersonaSection persona={article.persona} />
                        {article.topics?.length > 0 && (
                            <div className="md:w-1/2 min-w-0">
                                <TopicBadgeRow topics={article.topics} maxVisible={3} />
                            </div>
                        )}
                    </div>

                    {/* Chat panel — bottom-anchored, both mobile and desktop */}
                    {articleId && (
                        <div className="mt-6 mb-6" id="section-chat">
                            <ChatPanel user={user} articleId={articleId} article={article} />
                        </div>
                    )}

                    {/* Tags */}
                    {article.tags?.length > 0 && (
                        <div className="sports-article-tags mb-6 flex flex-wrap gap-2">
                            {article.tags.map((tag, idx) => (
                                <span
                                    key={idx}
                                    className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-theme-badge-primary-bg text-theme-badge-primary-text"
                                >
                                    #{tag}
                                </span>
                            ))}
                        </div>
                    )}

                    {/* Summary */}
                    {article.summary && (
                        <div id="section-summary" className="sports-article-summary mb-6 p-4 bg-theme-surface-muted rounded-lg border-l-4 border-theme-primary">
                            <p className="text-theme-text italic">
                                {article.summary}
                            </p>
                        </div>
                    )}


                    {/* Bias Analysis */}
                    <BiasIndicator bias={article.bias} biases={article.biases} />

                    {/* Compute image placement for interleaving */}
                    {(() => {
                        const bodyParagraphs = Array.isArray(article.body) ? article.body : [];
                        const { heroImage, imagesByParagraph } = computeImagePlacement(
                            article.generatedImages,
                            bodyParagraphs.length
                        );

                        // Helper to filter out malformed AI generation artifacts
                        const isValidSentence = (sent) => {
                            if (typeof sent !== 'string') return false;
                            const trimmed = sent.trim();
                            const invalidPatterns = [
                                /^[\]\}\[\{,\s]*$/,
                                /^\]?\}?,?\{?\s*$/,
                                /^sentences$/i,
                                /^biases$/i,
                                /^\]?\}?\],?\s*biases$/i,
                                /^score:\s*\d+\.?\d*$/i,
                                /^explanation:/i,
                                /^sourceName for bias:/i,
                                /^biasesResolved:/i,
                                /^Keywords for bias/i,
                            ];
                            if (trimmed.length < 10) return false;
                            for (const pattern of invalidPatterns) {
                                if (pattern.test(trimmed)) return false;
                            }
                            if (trimmed.startsWith('score:') || trimmed.startsWith('Bias:') || trimmed.startsWith('explanation:')) {
                                return false;
                            }
                            return true;
                        };

                        // Render a single paragraph
                        const renderParagraph = (paragraph, idx) => {
                            if (typeof paragraph === 'string') {
                                if (!isValidSentence(paragraph)) return null;
                                return (
                                    <p key={`para-${idx}`} className="mb-4 text-theme-text">
                                        {paragraph}
                                    </p>
                                );
                            }

                            if (paragraph.sentences && Array.isArray(paragraph.sentences)) {
                                const validSentences = paragraph.sentences
                                    .filter(isValidSentence)
                                    .map(s => s.trim().replace(/\s*\]?\}?,?\s*$/, '').trim());
                                if (validSentences.length === 0) return null;
                                return (
                                    <p key={`para-${idx}`} className="mb-4 text-theme-text">
                                        {validSentences.join(' ')}
                                    </p>
                                );
                            }

                            if (paragraph.heading || paragraph.content) {
                                return (
                                    <div key={`para-${idx}`} className="mb-4">
                                        {paragraph.heading && (
                                            <h2 className="text-xl font-semibold mb-2 text-theme-text">
                                                {paragraph.heading}
                                            </h2>
                                        )}
                                        {paragraph.content && (
                                            <p className="text-theme-text">
                                                {paragraph.content}
                                            </p>
                                        )}
                                    </div>
                                );
                            }

                            const stringVal = String(paragraph);
                            if (!isValidSentence(stringVal)) return null;
                            return (
                                <p key={`para-${idx}`} className="mb-4 text-theme-text">
                                    {stringVal}
                                </p>
                            );
                        };

                        return (
                            <div id="section-article">
                                {/* Hero Image */}
                                {heroImage && <ArticleImage image={heroImage} isHero={true} />}

                                {/* Article Body with interleaved images */}
                                <div className="sports-article-body prose prose-lg dark:prose-invert max-w-none mb-8">
                                    {bodyParagraphs.map((paragraph, idx) => {
                                        const paragraphElement = renderParagraph(paragraph, idx);
                                        const imagesAfterThisParagraph = imagesByParagraph[idx] || [];

                                        return (
                                            <React.Fragment key={`frag-${idx}`}>
                                                {paragraphElement}
                                                {imagesAfterThisParagraph.map((img, imgIdx) => (
                                                    <ArticleImage key={`img-${idx}-${imgIdx}`} image={img} />
                                                ))}
                                            </React.Fragment>
                                        );
                                    })}
                                    {typeof article.body === 'string' && (
                                        <p className="text-neutral-700 dark:text-neutral-300">{article.body}</p>
                                    )}
                                </div>
                            </div>
                        );
                    })()}

                    {/* Key Facts */}
                    {article.keyFacts?.length > 0 && (
                        <div id="section-facts" className="sports-article-key-facts mb-8 p-4 bg-green-50 dark:bg-green-900/20 rounded-lg">
                            <h3 className="text-lg font-semibold text-green-800 dark:text-green-200 mb-3">
                                Key Facts
                            </h3>
                            <ul className="list-disc list-inside space-y-1">
                                {article.keyFacts.map((fact, idx) => (
                                    <li key={idx} className="text-sm text-green-700 dark:text-green-300">
                                        {fact}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}

                    {/* Source Articles */}
                    {article.sourceArticlesProcessed?.length > 0 && (
                        <div id="section-sources" className="sports-article-sources mb-8">
                            <h3 className="text-lg font-semibold text-neutral-800 dark:text-neutral-200 mb-4">
                                Sources ({article.sourceArticlesProcessed.length})
                            </h3>
                            <div className="grid gap-3">
                                {article.sourceArticlesProcessed.map((source, idx) => (
                                    <SourceArticleCard key={source._id || idx} source={source} />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Keywords */}
                    {article.keyWords?.length > 0 && (
                        <div className="sports-article-keywords mb-8">
                            <h4 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                                Keywords
                            </h4>
                            <div className="flex flex-wrap gap-1">
                                {article.keyWords.map((keyword, idx) => (
                                    <span
                                        key={idx}
                                        className="text-xs px-2 py-0.5 bg-neutral-100 dark:bg-neutral-800 text-neutral-600 dark:text-neutral-400 rounded"
                                    >
                                        {keyword}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Navigation */}
                    <div className="sports-article-navigation border-t border-neutral-200 dark:border-neutral-700 pt-6">
                        {renderLink(
                            '/',
                            'inline-flex items-center text-blue-600 dark:text-blue-400 hover:underline',
                            <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                                </svg>
                                Back to Articles
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

/**
 * Main component export
 */
const SportsArticleDetailPage = () => {
    // Get user's article layout preference
    const userLayoutPref = useSelector(
        (state) => state.pageLayoutSettings?.userLayoutPreferences?.articleDetail || 'default'
    );
    const ArticleLayoutWrapper = useMemo(() => getArticleLayout(userLayoutPref), [userLayoutPref]);

    return (
        <main className="sports-article-page min-h-screen bg-theme-surface">
            <ArticleLayoutWrapper>
                <SportsArticleDetailContent />
            </ArticleLayoutWrapper>
            <ArticleNavBar />
        </main>
    );
};

export default SportsArticleDetailPage;
